import {
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/core';
import {
    PROMO_PRICE_SIZE_LARGE,
    PROMO_PRICE_SIZE_MEDIUM,
} from '@vfde-brix/promo-price';
import { getCookieValue } from '@vfde-fe/sails_core';
import {
    ADDITIONAL_PAGE_OPTIONS,
    RED_XS_GROUP,
    SalesChannel,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_REDPLUS,
    SALESCHANNEL_SOHO,
} from '@vfde-fe/sails-constants';
import joinArrayWith from 'Helper/joinArrayWith';
import {
    calculateDiscountedMonthlyPrice,
    getStrikePrice,
    shouldShowStrikePrice,
} from '../../Tariff/helper/priceHelper';
import {
    MonthlyPrice,
    OfferPriceParameters,
    StateProps,
} from '../interface';
import replaceCMSPlaceholder from '../../../helpers/replaceCMSPlaceholder';
import { XSELL_PP_VOUCHER_COOKIE_KEY } from '../constants';

/**
 * Compute conditions for showing or hiding promo price elements
 * @param hasSubscription
 * @param isHardwareOnly
 */
export const shouldHideTariffInformation = (hasSubscription: boolean, isHardwareOnly: boolean): boolean => {
    return isHardwareOnly || !hasSubscription;
};

/**
 * Monthly price should be shown if insurance is set
 * @param hasSubscription
 * @param isHardwareOnly
 * @param hasInsurance
 */
export const shouldHideMonthlyPrice = (hasSubscription: boolean, isHardwareOnly: boolean, hasInsurance: boolean): boolean => {
    return (!hasSubscription && !hasInsurance) || isHardwareOnly;
};

/**
 * Get monthly price
 * @param state
 */
export const getMonthlyPrice = (state: Partial<StateProps>): Partial<MonthlyPrice> => {
    const { offer } = state;
    const price: Partial<MonthlyPrice> = {};
    const strikePrice = offer.tariffPrice && shouldShowStrikePrice(state) ? getStrikePrice(state) : undefined;
    const monthlyPrice = calculateDiscountedMonthlyPrice(state);

    if (strikePrice) {
        price.strikePrice = Price.fromNumber(strikePrice);
    }

    if (monthlyPrice) {
        price.price = Price.fromNumber(monthlyPrice);
    }

    return price;
};

/**
 * Get one time price
 * @param price
 * @param isHardwareOnly
 */
export const getOneTimePrice = (price, isHardwareOnly: boolean): number => {
    if (isHardwareOnly) {
        return price.totalPrice.gross;
    }

    return price.oneTimePrice.gross;
};

/**
 * Get size variation for price
 * @param hasSubscription
 * @param isHardwareOnly
 * @param hasInsurance
 */
export const getPriceSizeVariation = (hasSubscription, isHardwareOnly: boolean, hasInsurance: boolean): typeof PROMO_PRICE_SIZE_LARGE|typeof PROMO_PRICE_SIZE_MEDIUM => {
    if (shouldHideMonthlyPrice(hasSubscription, isHardwareOnly, hasInsurance)) {
        return PROMO_PRICE_SIZE_LARGE;
    }

    return PROMO_PRICE_SIZE_MEDIUM;
};

/**
 * Get price condition
 * @param hasSubscription
 * @param isHardwareOnly
 */
export const getPriceCondition = (hasSubscription: boolean, isHardwareOnly: boolean): string => {
    if (!hasSubscription && !isHardwareOnly) {
        return window[ADDITIONAL_PAGE_OPTIONS].offerPrice.priceCondition;
    }

    return '';
};

/**
 * Get button text and link
 * @param state
 * @param business
 */
export const getPrimaryButtonContent = ({offer, subscriptionId, salesChannel, isGigakombi, isGigakombiEligible, isHardwareOnly, atomicId, hasInsurance }: Partial<StateProps>, { triggerGigaKombiOverlay = undefined, goToBasket = undefined } = {}) => {
    const { hardwareOnly, offerPrice, gigakombi, familyFriends, redplus } = window[ADDITIONAL_PAGE_OPTIONS];
    const hasSubscription = !!subscriptionId;
    const hasXsellPPVoucherCode = !!getCookieValue(XSELL_PP_VOUCHER_COOKIE_KEY);
    const eligibleForXsellPP = hasXsellPPVoucherCode && subscriptionId === RED_XS_GROUP.toString();
    const buttonContent: any = {
        href: window[ADDITIONAL_PAGE_OPTIONS].tariffOverview[salesChannel],
        content: offerPrice.goToTariffOverviewButton,
    };

    if (isHardwareOnly || hasSubscription) {
        buttonContent.content = hardwareOnly.offerPrice.primaryButtonText;
        buttonContent.href = hardwareOnly.offerPrice.primaryButtonLink;
        buttonContent.business = NO_PATTERN_BUSINESS_LOGIC;
    }

    if (salesChannel === SALESCHANNEL_FAMILY_FRIENDS && hasSubscription && !isHardwareOnly) {
        buttonContent.content = familyFriends.offerPrice.primaryButtonText;
        buttonContent.href = undefined;
        buttonContent.business = { onClick: goToBasket };
    }

    if (salesChannel === SALESCHANNEL_REDPLUS && hasSubscription && !isHardwareOnly) {
        buttonContent.content = redplus.offerPrice.primaryButtonText;
        buttonContent.href = undefined;
        buttonContent.business = { onClick: goToBasket };
    }

    if (isGigakombi && !isHardwareOnly && !eligibleForXsellPP) {
        buttonContent.content = gigakombi.offerPrice.basketButtonText;
        buttonContent.href = undefined;
        buttonContent.business = { onClick: triggerGigaKombiOverlay };
    }

    if (!isHardwareOnly && (isGigakombiEligible || (isGigakombi && eligibleForXsellPP))) {
        buttonContent.content = 'Zum Warenkorb'; // TODO: move as default to additionalPageOptions
        buttonContent.href = undefined;
        buttonContent.business = { onClick: goToBasket };
    }

    return buttonContent;
};

/**
 * Get headline
 *
 * @param name
 * @param parameters
 */
export const getHeadline = (name, parameters: OfferPriceParameters): string => {
    const { hasInsurance, isHardwareOnly } = parameters;
    const { offerPrice } = window[ADDITIONAL_PAGE_OPTIONS];
    const { insuranceTitle, withAddons } = offerPrice;
    const addons = [];

    hasInsurance && !isHardwareOnly && addons.push(insuranceTitle);

    return addons.length === 0 ? name : replaceCMSPlaceholder(withAddons, {
        name,
        addons: joinArrayWith(addons, '&amp;'),
    });
};

/**
 * Get subline text
 * @param offer
 * @param isHardwareOnly
 * @param hasSubscription
 */
export const getSubline = (offer, isHardwareOnly: boolean, hasSubscription: boolean): string => {
    if (hasSubscription && !isHardwareOnly) {
        const {dataVolume, dataPush} = offer;
        const data = (dataPush.length > 0) ? dataPush[0] : dataVolume;

        return data.amount === 0 && !offer.unlimited ? offer.name : `${offer.name} mit ${getDataAmount(data, offer.unlimited)}`;
    }

    if (isHardwareOnly) {
        return window[ADDITIONAL_PAGE_OPTIONS].hardwareOnly.offerPrice.tariffSubline;
    }

    return window[ADDITIONAL_PAGE_OPTIONS].offerPrice.tariffSubline;
};

/**
 * Get highlight badge text
 * @param subscriptionId
 * @param isHardwareOnly
 * @param isGigakombi
 * @param salesChannel
 */
export const getHighlightBadgeContent = (subscriptionId: string, isHardwareOnly: boolean, isGigakombi: boolean, salesChannel: SalesChannel) => {
    const contentKey = subscriptionId || 'default';
    const { hardwareOnly, highlightBadges, gigakombi, familyFriends, soho, redplus } = window[ADDITIONAL_PAGE_OPTIONS];

    if (isHardwareOnly) {
        return hardwareOnly?.highlightBadge;
    }

    if (salesChannel === SALESCHANNEL_FAMILY_FRIENDS && familyFriends.highlightBadges?.[contentKey]) {
        return familyFriends.highlightBadges[contentKey];
    }

    if (salesChannel === SALESCHANNEL_SOHO && soho.highlightBadges?.[contentKey] && !isGigakombi) {
        return soho.highlightBadges[contentKey];
    }

    if (salesChannel === SALESCHANNEL_REDPLUS && redplus.highlightBadges?.[contentKey]) {
        return redplus.highlightBadges[contentKey];
    }

    if (isGigakombi && gigakombi.highlightBadges?.[contentKey]) {
        return gigakombi.highlightBadges[contentKey];
    }

    return highlightBadges[contentKey];
};

/**
 * Should hide VVL link
 * @param salesChannel
 */
export const shouldHideVvlLink = (salesChannel: SalesChannel) => [
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_REDPLUS,
].indexOf(salesChannel) !== -1;

const getDataAmount = (amount, unlimited: boolean = false) => unlimited ? amount.label : `${amount.amount.toLocaleString()} ${amount.unit}`;
